// circle — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 50%;
    box-sizing: border-box;
    width: var(--run-circle-size, 2.5rem);
    height: var(--run-circle-size, 2.5rem);
    font-size: var(--run-circle-font-size, var(--run-font-size-base));
    font-weight: var(--run-circle-font-weight, var(--run-font-semantic-weight-body));
    line-height: var(--run-circle-line-height, var(--run-font-semantic-line-height-body));
    box-shadow: var(--run-circle-shadow, var(--run-elevation-semantic-none));
  }

  /* variant=outline (default) */
  :host {
    background: var(--run-circle-bg, transparent);
    border: var(--run-circle-border-width, 1px) solid var(--run-circle-border-color, var(--run-color-border-default));
    color: var(--run-circle-fg, var(--run-color-text-primary));
  }

  /* variant=solid */
  :host([variant="solid"]) {
    background: var(--run-circle-bg, var(--run-color-intent-neutral-base));
    border: var(--run-circle-border-width, 1px) solid transparent;
    color: var(--run-circle-fg, var(--run-color-text-primary));
  }

  /* variant=ghost */
  :host([variant="ghost"]) {
    background: var(--run-circle-bg, transparent);
    border: var(--run-circle-border-width, 1px) solid transparent;
    color: var(--run-circle-fg, var(--run-color-text-primary));
  }

  /* intent=neutral */
  :host([intent="neutral"]) {
    background: var(--run-circle-bg, transparent);
    border-color: var(--run-circle-border-color, var(--run-color-border-default));
    color: var(--run-circle-fg, var(--run-color-text-primary));
  }
  :host([variant="solid"][intent="neutral"]) {
    background: var(--run-circle-bg, var(--run-color-intent-neutral-base));
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-text-primary));
  }
  :host([variant="ghost"][intent="neutral"]) {
    background: var(--run-circle-bg, transparent);
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-text-primary));
  }

  /* intent=primary */
  :host([intent="primary"]) {
    background: var(--run-circle-bg, transparent);
    border-color: var(--run-circle-border-color, var(--run-color-intent-primary-base));
    color: var(--run-circle-fg, var(--run-color-intent-primary-base));
  }
  :host([variant="solid"][intent="primary"]) {
    background: var(--run-circle-bg, var(--run-color-intent-primary-base));
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-intent-primary-on));
  }
  :host([variant="ghost"][intent="primary"]) {
    background: var(--run-circle-bg, transparent);
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-intent-primary-base));
  }

  /* intent=success */
  :host([intent="success"]) {
    background: var(--run-circle-bg, transparent);
    border-color: var(--run-circle-border-color, var(--run-color-intent-success-base));
    color: var(--run-circle-fg, var(--run-color-intent-success-base));
  }
  :host([variant="solid"][intent="success"]) {
    background: var(--run-circle-bg, var(--run-color-intent-success-base));
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-text-inverse));
  }
  :host([variant="ghost"][intent="success"]) {
    background: var(--run-circle-bg, transparent);
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-intent-success-base));
  }

  /* intent=warning */
  :host([intent="warning"]) {
    background: var(--run-circle-bg, transparent);
    border-color: var(--run-circle-border-color, var(--run-color-intent-warning-base));
    color: var(--run-circle-fg, var(--run-color-intent-warning-base));
  }
  :host([variant="solid"][intent="warning"]) {
    background: var(--run-circle-bg, var(--run-color-intent-warning-base));
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-text-primary));
  }
  :host([variant="ghost"][intent="warning"]) {
    background: var(--run-circle-bg, transparent);
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-intent-warning-base));
  }

  /* intent=danger */
  :host([intent="danger"]) {
    background: var(--run-circle-bg, transparent);
    border-color: var(--run-circle-border-color, var(--run-color-intent-danger-base));
    color: var(--run-circle-fg, var(--run-color-intent-danger-base));
  }
  :host([variant="solid"][intent="danger"]) {
    background: var(--run-circle-bg, var(--run-color-intent-danger-base));
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-intent-danger-on));
  }
  :host([variant="ghost"][intent="danger"]) {
    background: var(--run-circle-bg, transparent);
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-intent-danger-base));
  }

  /* intent=info */
  :host([intent="info"]) {
    background: var(--run-circle-bg, transparent);
    border-color: var(--run-circle-border-color, var(--run-color-intent-info-base));
    color: var(--run-circle-fg, var(--run-color-intent-info-base));
  }
  :host([variant="solid"][intent="info"]) {
    background: var(--run-circle-bg, var(--run-color-intent-info-base));
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-text-inverse));
  }
  :host([variant="ghost"][intent="info"]) {
    background: var(--run-circle-bg, transparent);
    border-color: transparent;
    color: var(--run-circle-fg, var(--run-color-intent-info-base));
  }

  slot {
    display: contents;
  }
`;

export function render(host) {
  const { size, variant, intent } = host; // declared in MVA SkinModule.render contract
  host.shadowRoot.innerHTML = `<slot></slot>`;
}